(ns ui.view.entry-criteria.utils
  "Frontend-side helpers for the stage-entry-criteria feature.

   Wraps `common.schema.entry-criteria.utils` (cljc) with cljs-only
   conveniences. Lives alongside the modal namespaces under
   `ui.view.entry-criteria.*` and is shared by the modal code and by the
   `frontend.view.entity/attempt-move-to-stage!` trigger fn.

   See docs/functional/stage_entry_criteria/ for the design."
  (:require [common.schema.entity-creator.utils :as ecu]
            [common.schema.entities.pipeline :as pipeline]
            [common.schema.entry-criteria.utils :as entry-u]
            [frontend.state.current-session :as session]
            [frontend.state.permissions :as f-permissions]
            [ui.view.entity-creator.utils :as creator-u]))

(defn association-scope
  "Given the source-entity-type of an association config item and the
   entity-types of the opportunity / target / target-profile entities,
   returns which scope the association is anchored on, or nil if no scope
   matches."
  [{:keys [source-entity-type
           opportunity-entity-type
           target-entity-type
           target-profile-entity-type]}]
  (cond
    (= source-entity-type opportunity-entity-type) :opportunity
    (= source-entity-type target-entity-type) :target
    (= source-entity-type target-profile-entity-type) :target-profile))

(defn with-association-scopes
  "Enriches each item in a resolved associations-stage-config with `:scope`
   derived from its `:association-key`'s source-entity-type. Items whose
   source-entity-type doesn't match any of the three scopes — phantom
   associations left over from configuration drift, e.g. a sales-pipeline
   flipped from investor-only to `target-asset-types #{company}` while an
   investor-anchored association is still in the creator-config — keep
   `:scope` nil and are dropped by `entry-u/association-criteria`."
  [associations-config entity-types]
  (update associations-config :associations
          (fn [associations]
            (mapv (fn [{:keys [association-key] :as assoc-config}]
                    (assoc assoc-config
                           :scope (association-scope
                                   (assoc entity-types
                                          :source-entity-type
                                          (first association-key)))))
                  associations))))

(defn resolve-stage-entry-criteria
  "Frontend entry point for the cljc validator. Adds `:scope` to each item
   in `:associations-stage-config` from the surrounding entity-types, then
   delegates. All other inputs pass through unchanged.

   `:entity-types` is a map of `{:opportunity-entity-type ..,
   :target-entity-type .., :target-profile-entity-type ..}`."
  [{:keys [associations-stage-config entity-types] :as args}]
  (entry-u/resolve-stage-entry-criteria
   (cond-> args
     associations-stage-config
     (assoc :associations-stage-config
            (with-association-scopes associations-stage-config entity-types)))))

(def scopes
  "Canonical order of the three modal scopes — iterate this when rendering
   per-scope sections or walking scope-keyed maps so a new scope only needs
   to be added in one place."
  [:opportunity :target :target-profile])

(defn scope-entity-type
  "Returns the entity-type for `scope` resolved from a `move-ctx`."
  [{:keys [entity-type target-type target-profile-type]} scope]
  (case scope
    :opportunity entity-type
    :target target-type
    :target-profile target-profile-type))

(defn scope-config-path
  "Returns the creator-config sub-path for `scope` resolved from a `move-ctx`."
  [{:keys [config-paths]} scope]
  (case scope
    :opportunity (:main-config-path config-paths)
    :target (:target-config-path config-paths)
    :target-profile (:target-profile-config-path config-paths)))

(defn resolve-pipeline-entity
  "Returns the pipeline session entity for a stage-move attempt. When
   `target-pipeline` is provided (change-pipeline flow) it wins, even when the
   opportunity still references its source pipeline. Accepts either a pipeline
   id or entity-map."
  [{:keys [opportunity target-pipeline]}]
  (if target-pipeline
    (some-> (or (:db/id target-pipeline) target-pipeline) session/get-entity)
    (let [pipeline-attr (pipeline/pipeline-key (:entity/type opportunity))]
      (some-> (get opportunity pipeline-attr) session/get-entity))))

(defn missing-required-target?
  "True iff the (effective) pipeline disallows target-less opportunities and
   the opportunity has no target. The stage move can't proceed in this state.
   When `:target-pipeline` is supplied (change-pipeline flow), its
   `:pipeline/allow-without-target?` governs, not the source pipeline's."
  [{:keys [opportunity] :as args}]
  (let [pipeline-entity (resolve-pipeline-entity args)
        allow-without-target? (:pipeline/allow-without-target? pipeline-entity)
        target-attr (pipeline/target-attribute (:entity/type opportunity))
        target-id (get opportunity target-attr)]
    (and (not allow-without-target?)
         (not target-id))))

(defn move-context
  "Pulls everything the validator needs from session into a single map, so the
   per-scope resolvers and `write-access?` fn can share the work. Computed
   once per stage-move attempt.

   `:target-pipeline` (optional) overrides the opportunity's current pipeline
   — used by the change-pipeline flow so criteria, write-access, and target-
   profile resolution all run against the pipeline the user is moving into,
   not the one they're leaving. Accepts an id or an entity-map.

   Handles the target-profile inversion: for froppo / exit-oppo / sales-oppo
   on an investor-only pipeline, the directly-referenced entity (the
   investor) is the target-profile, and the actual target is the investor's
   `:investor/legal-entity`."
  [{:keys [opportunity] :as args}]
  (let [entity-type (:entity/type opportunity)
        pipeline-entity (resolve-pipeline-entity args)
        pipeline-id (:db/id pipeline-entity)
        primary-attr (pipeline/target-attribute entity-type)
        primary-entity (some-> (get opportunity primary-attr) session/get-entity)
        primary-type (:entity/type primary-entity)
        target-profile? (ecu/target-profile?
                         {:pipeline pipeline-entity
                          :entity-type entity-type
                          :target-type primary-type})
        [target-entity target-profile-entity]
        (if target-profile?
          [(some-> (:investor/legal-entity primary-entity) session/get-entity)
           primary-entity]
          [primary-entity nil])
        target-type (:entity/type target-entity)
        target-profile-type (ecu/resolve-target-profile-type
                             {:pipeline pipeline-entity
                              :entity-type entity-type
                              :target-type target-type})
        tenant @session/tenant
        ctx {:tenant tenant
             :tenant-schema @session/tenant-schema
             :pipeline pipeline-entity
             :field-configs (:tenant/field-configurations tenant)}
        stages (->> (session/pipeline-stages pipeline-entity)
                    (sort-by :common/ord))
        config-paths (ecu/resolve-config-paths
                      {:entity-type entity-type
                       :target-type target-type
                       :pipeline pipeline-entity})]
    {:entity-type entity-type
     :pipeline-id pipeline-id
     :pipeline-entity pipeline-entity
     :target-entity target-entity
     :target-type target-type
     :target-profile-entity target-profile-entity
     :target-profile-type target-profile-type
     :ctx ctx
     :stages stages
     :full-config (:pipeline/creator-config pipeline-entity)
     :config-paths config-paths}))

(defn scope-fields-stage-config
  "Resolve fields-stage-config for one scope (`:opportunity` / `:target` /
   `:target-profile`). Returns nil if the scope's entity-type or config-path
   is missing."
  [{:keys [ctx stages full-config] :as move-ctx} scope]
  (let [scope-et (scope-entity-type move-ctx scope)
        config-path (scope-config-path move-ctx scope)]
    (when (and scope-et config-path)
      (let [config (get-in full-config config-path)
            system-mandatory (-> (ecu/mandatory-fields ctx scope-et)
                                 keys
                                 set)]
        (ecu/resolve-fields-stage-config
         {:config config
          :system-mandatory system-mandatory
          :stages stages})))))

(defn editor-fields-config
  "Resolves the editor-side fields-config for one scope — same shape that
   `ui.view.entity-creator.sub/config` (the `m/config` sub) would produce on
   render. Calling this eagerly here lets the modal trim it against the
   criteria result before handing it to `editor/view`, so render and validation
   subscribe to the same `:fields-config` override.

   This includes the `ecu/static-fields` injection (e.g. person's
   first-name/last-name/email at the top), unlike `scope-fields-stage-config`
   which goes directly through the cljc resolver. Returns nil when the scope
   has no entity-type or no config-path."
  [{:keys [ctx pipeline-entity stages full-config target-type] :as move-ctx} scope]
  (let [scope-et (scope-entity-type move-ctx scope)
        config-path (scope-config-path move-ctx scope)]
    (when (and scope-et config-path)
      (let [system-mandatory (-> (ecu/mandatory-fields ctx scope-et)
                                 keys
                                 set)]
        (creator-u/resolve-entity-config
         {:entity-type scope-et
          :target-type target-type
          :config-path config-path
          :raw-config full-config
          :stages stages
          :pipeline pipeline-entity
          :system-mandatory system-mandatory})))))

(defn fillable-criteria
  "The criteria for `scope` the acting user can actually act on.

   A write-disabled criterion cannot be surfaced as an input: the editor drops
   hidden fields, so including it produced a scope block reading \"No fields
   configured\" with Confirm disabled and no way forward. Those are listed as
   blockers instead - see `blocked-field-criteria`."
  [criteria scope]
  (->> criteria
       (filter (fn [criterion] (= scope (:scope criterion))))
       (remove :write-disabled?)))

(defn blocked-field-criteria
  "Field criteria the acting user has no write access to, across all scopes.
   These are what the modal names as blockers. Associations are excluded -
   they have their own section and their own write-disabled suffix - as are
   criteria carrying no attribute to name."
  [criteria]
  (->> criteria
       (filter :write-disabled?)
       (remove (fn [{:keys [kind]}] (= :association-missing kind)))
       (filter (fn [{:keys [attribute attributes]}]
                 (or attribute (seq attributes))))))

(defn review-attributes
  "The attributes that a review (`:review` of the backend's answer) asks the
   modal to show for `scope`, as a set. Empty without a review."
  [review scope]
  (set (get-in review [:attributes scope])))

(defn trimmed-fields-config-for-scope
  "Returns the editor-side resolved fields-config trimmed to only the fields
   the modal should surface for this scope: those mentioned by a single-field
   criterion plus the participating attributes of any group criterion. With a
   `:review` it also surfaces each attribute that the review lists, so the
   modal shows the fields that are filled and the optional ones too.
   Preserves the editor's original ordering so any always-first injections
   (e.g. person name/email) stay first. Returns nil when nothing needs
   surfacing in this scope."
  [{:keys [move-ctx scope criteria review]}]
  (let [scope-criteria (fillable-criteria criteria scope)
        must-show (into (entry-u/criteria-field-set scope-criteria)
                        (review-attributes review scope))]
    (when (seq must-show)
      (some-> (editor-fields-config move-ctx scope)
              (entry-u/filter-fields-config must-show)))))

(defn review-association-criteria
  "The optional association rows that a review adds to the modal, in the shape
   of a criterion so the association rows treat both alike. Kind
   `:association-optional` tells a row that it must not block Confirm. A
   missing mandatory association is a criterion of kind `:association-missing`
   already, and is not repeated here."
  [review]
  (->> (:associations review)
       (mapv (fn [association]
               (assoc association
                      :kind :association-optional
                      :write-disabled? false)))))

(defn association-rows
  "All association rows the modal shows: the criteria that are missing
   associations, then the optional rows of a review."
  [{:keys [criteria review]}]
  (into (filterv (fn [criterion]
                   (= :association-missing (:kind criterion)))
                 criteria)
        (review-association-criteria review)))

(defn associations-stage-config
  "Resolve the associations-stage-config for the opportunity's pipeline. The
   result is a single block per the spec — `with-association-scopes` injects
   `:scope` into each item based on the source-entity-type."
  [{:keys [stages full-config config-paths]}]
  (let [config-path (:associations-config-path config-paths)]
    (when config-path
      (ecu/resolve-associations-stage-config
       {:config (get-in full-config config-path)
        :stages stages}))))

(defn pull-entity-associations
  "Returns the `:entity.type/entity-association` entities the given scope
   entity is part of (either as source or target), with disabled entries
   removed. Returns nil when entity is nil so the validator records mandatory
   associations as missing."
  [entity]
  (some->> entity
           :common/related-entities
           (keep (fn [ref]
                   (session/get-entity (or (:db/id ref) ref))))
           (filter #(= :entity.type/entity-association (:entity/type %)))
           (remove :common/disabled?)))

(defn associations-by-scope
  "Pulls per-scope entity-associations from session. Each value is a seq of
   `:entity.type/entity-association` entities the scope entity participates
   in; the validator matches them against the config's `:association-type`."
  [{:keys [opportunity-entity target-entity target-profile-entity]}]
  {:opportunity (pull-entity-associations opportunity-entity)
   :target (pull-entity-associations target-entity)
   :target-profile (pull-entity-associations target-profile-entity)})

(defn write-access-fn
  "Builds the `:write-access?` fn the cljc validator expects. For each scope
   it queries `frontend.state.permissions/field-write-access?` with that
   scope's entity-type and the opportunity's pipeline-id."
  [{:keys [pipeline-id] :as move-ctx}]
  (fn [scope attribute]
    (let [scope-et (scope-entity-type move-ctx scope)]
      (if scope-et
        (f-permissions/field-write-access? attribute scope-et pipeline-id)
        true))))

(defn criteria-for-stage-move
  "Frontend entry point used by `frontend.view.entity/attempt-move-to-stage!`.
   Resolves opportunity- / target- / target-profile-scope fields-stage-config
   and the associations-stage-config from session, plus per-scope pulled
   entity-associations, then calls the cljc validator.

   `:target-pipeline` (optional) — change-pipeline flow override; criteria
   resolve against this pipeline instead of the opportunity's current one."
  [{:keys [opportunity target-stage target-pipeline]}]
  (let [ctx (move-context {:opportunity opportunity
                           :target-pipeline target-pipeline})
        target-stage-id (or (:db/id target-stage) target-stage)]
    (resolve-stage-entry-criteria
     {:opportunity opportunity
      :target (:target-entity ctx)
      :target-profile (:target-profile-entity ctx)
      :target-stage-id target-stage-id
      :opportunity-fields-stage-config (scope-fields-stage-config ctx :opportunity)
      :target-fields-stage-config (scope-fields-stage-config ctx :target)
      :target-profile-fields-stage-config (scope-fields-stage-config ctx :target-profile)
      :associations-stage-config (associations-stage-config ctx)
      :associations-by-scope (associations-by-scope
                              {:opportunity-entity opportunity
                               :target-entity (:target-entity ctx)
                               :target-profile-entity (:target-profile-entity ctx)})
      :entity-types {:opportunity-entity-type (:entity-type ctx)
                     :target-entity-type (:target-type ctx)
                     :target-profile-entity-type (:target-profile-type ctx)}
      :write-access? (write-access-fn ctx)})))
